/**
 * Named WebGL blend factor tokens. Mapped to actual gl.* constants at render time.
 */
export type BlendFactor =
  | 'ONE'
  | 'ZERO'
  | 'SRC_ALPHA'
  | 'ONE_MINUS_SRC_ALPHA'
  | 'DST_ALPHA'
  | 'ONE_MINUS_DST_ALPHA';

export const blendFactorLabels: Record<BlendFactor, string> = {
  ONE: 'ONE',
  ZERO: 'ZERO',
  SRC_ALPHA: 'SRC_ALPHA',
  ONE_MINUS_SRC_ALPHA: 'ONE_MINUS_SRC_ALPHA',
  DST_ALPHA: 'DST_ALPHA',
  ONE_MINUS_DST_ALPHA: 'ONE_MINUS_DST_ALPHA',
};

export type ExtrusionRenderParams = {
  /** Source blend factor for all extrusion passes. */
  blendSrc: BlendFactor;
  /** Destination blend factor for all extrusion passes. */
  blendDst: BlendFactor;
  /** Floor fill opacity for storeys above ground (0–1). */
  floorOpacity: number;
  /** Floor fill opacity for the ground storey (0–1). */
  groundFloorOpacity: number;
  /** Roof plane opacity (0–1). */
  roofOpacity: number;
  /** Facade wall opacity above ground (0–1). */
  wallOpacity: number;
  /** Whether to render facade walls. */
  showWalls: boolean;
  /** Whether to render roof planes. */
  showRoofs: boolean;
  /** Whether floor planes write to the depth buffer. Disable for alpha accumulation across storeys. */
  depthMaskFloors: boolean;
  /** Whether wall planes write to the depth buffer. */
  depthMaskWalls: boolean;
  /** Whether roof planes write to the depth buffer. */
  depthMaskRoofs: boolean;
  /** Roof plane fill color (RGB 0–1). */
  roofColor: readonly [number, number, number];
  /** Facade wall color above ground (RGB 0–1). */
  wallColor: readonly [number, number, number];
  /** Wall color below ground (RGB 0–1). */
  undergroundWallColor: readonly [number, number, number];
  /** When true, use {@link floorColor} instead of the per-activity color for floor fills. */
  useFloorColorOverride: boolean;
  /** Override color for all floor fills (RGB 0–1). Active when {@link useFloorColorOverride} is true. */
  floorColor: readonly [number, number, number];
  /** When true, use the light values below instead of resolving from the Mapbox style. */
  useLightOverride: boolean;
  /** Ambient light floor (0–1). Active when {@link useLightOverride} is true. */
  lightAmbient: number;
  /** Diffuse shading strength (0–1). Active when {@link useLightOverride} is true. */
  lightStrength: number;
  /** Light direction X component (local mercator, east). Active when {@link useLightOverride} is true. */
  lightDirX: number;
  /** Light direction Y component (local mercator, south). Active when {@link useLightOverride} is true. */
  lightDirY: number;
  /** Light direction Z component (local mercator, up). Active when {@link useLightOverride} is true. */
  lightDirZ: number;
};

export const defaultExtrusionRenderParams: ExtrusionRenderParams = {
  blendSrc: 'ONE',
  blendDst: 'ONE_MINUS_SRC_ALPHA',
  floorOpacity: 0.6,
  groundFloorOpacity: 0.8,
  roofOpacity: 0.5,
  wallOpacity: 0.2,
  showWalls: true,
  showRoofs: true,
  depthMaskFloors: false,
  depthMaskWalls: false,
  depthMaskRoofs: false,
  useLightOverride: false,
  // Defaults derived from resolveExtrusionLightSettings at intensity=0.5
  lightAmbient: 0.92,
  lightStrength: 0.14,
  lightDirX: 0.42,
  lightDirY: 0.63,
  lightDirZ: 0.74,
  roofColor: [1, 1, 1],
  wallColor: [1, 1, 1],
  undergroundWallColor: [0.9, 0.9, 0.9],
  useFloorColorOverride: false,
  floorColor: [0.5, 0.7, 0.9],
};

/**
 * Convert a {@link BlendFactor} token to the corresponding WebGL enum value.
 */
export const resolveGlBlendFactor = (
  gl: WebGLRenderingContext,
  factor: BlendFactor,
): number => {
  const map: Record<BlendFactor, number> = {
    ONE: gl.ONE,
    ZERO: gl.ZERO,
    SRC_ALPHA: gl.SRC_ALPHA,
    ONE_MINUS_SRC_ALPHA: gl.ONE_MINUS_SRC_ALPHA,
    DST_ALPHA: gl.DST_ALPHA,
    ONE_MINUS_DST_ALPHA: gl.ONE_MINUS_DST_ALPHA,
  };
  return map[factor];
};
